<template>
  <div
    v-if="$store.getters.getAuth && $route.path !== '/login'"
    class="navigation-shell"
  >
  <aside id="sidebar" aria-label="Navegación principal">
    <router-link to="/" class="sidebar-brand" aria-label="Ir al inicio">
      <img src="/img/logo_osfatun.svg" alt="OSFATUN" />
    </router-link>

    <nav class="sidebar-navigation">
      <ul>
        <li v-for="item in visibleMenuItems" :key="item.key" class="sidebar-entry">
          <component
            :is="item.children ? 'button' : 'router-link'"
            v-bind="item.children ? buttonBindings(item) : { to: item.to }"
            class="sidebar-item"
            :class="{ 'is-active': isSectionActive(item) }"
            @click="item.children && toggleMenu(item.key)"
          >
            <span class="sidebar-icon" :class="{ 'is-ventas': item.key === 'ventas' }">
              <template v-if="item.key === 'ventas'">
                <img class="ventas-user" :src="item.icon[0]" alt="" />
                <img class="ventas-plus" :src="item.icon[1]" alt="" />
                <img class="ventas-head" :src="item.icon[2]" alt="" />
              </template>
              <img v-else :src="item.icon" alt="" />
            </span>
            <span>{{ item.label }}</span>
          </component>

          <transition name="sidebar-popover">
            <ul
              v-if="item.children"
              v-show="openMenu === item.key"
              :id="`${item.key}-submenu`"
              class="sidebar-submenu"
              :aria-label="item.label"
            >
              <li v-for="child in visibleChildren(item.children)" :key="child.label">
                <a
                  v-if="child.external"
                  :href="child.to"
                  target="_blank"
                  rel="noopener noreferrer"
                  @click="closeMenu"
                >
                  {{ child.label }}
                  <span class="sr-only">(abre en una pestaña nueva)</span>
                </a>
                <router-link v-else :to="child.to" @click.native="closeMenu">
                  {{ child.label }}
                </router-link>
              </li>
            </ul>
          </transition>
        </li>
      </ul>
    </nav>

    <div class="sidebar-footer">
      <b-dropdown class="profile-dropdown" aria-role="menu" close-on-click animation="fade">
        <template #trigger>
          <button class="profile-trigger" aria-label="Abrir menú de usuario" @click="closeMenu">
            <figure class="image is-48x48">
              <img class="is-rounded" :src="avatar" alt="Foto de perfil" />
            </figure>
          </button>
        </template>
        <b-dropdown-item
          v-if="$hasPermission('agentes.editarmiperfil')"
          custom
          aria-role="menuitem"
        >
          <router-link
            :to="`/agentes/editar/${$store.getters.getSession.id}`"
            class="is-uppercase has-text-weight-bold"
          >
            Mi perfil
          </router-link>
        </b-dropdown-item>
        <b-dropdown-item custom aria-role="menuitem">
          <a href="#" class="is-uppercase has-text-weight-bold" @click.prevent="openDashboardNotices">
            Notificaciones
          </a>
        </b-dropdown-item>
        <hr class="dropdown-divider" />
        <b-dropdown-item custom aria-role="menuitem">
          <a href="#" class="is-uppercase has-text-weight-bold" @click.prevent="logout">
            Cerrar sesión
          </a>
        </b-dropdown-item>
      </b-dropdown>

      <router-link to="/" class="zaro-brand" aria-label="Ir al inicio">
        <img src="/img/logo_zaro.png" alt="Zaro" />
      </router-link>
    </div>
  </aside>

  <header class="mobile-topbar">
    <router-link to="/" class="mobile-brand" aria-label="Ir al inicio">
      <img src="/img/logo_osfatun.svg" alt="OSFATUN" />
    </router-link>

    <b-dropdown
      class="mobile-profile-dropdown"
      aria-role="menu"
      close-on-click
      animation="fade"
      :mobile-modal="false"
    >
      <template #trigger>
        <button class="mobile-profile-trigger" aria-label="Abrir menú de usuario">
          <img :src="avatar" alt="Foto de perfil" />
        </button>
      </template>
      <b-dropdown-item
        v-if="$hasPermission('agentes.editarmiperfil')"
        custom
        aria-role="menuitem"
      >
        <router-link :to="`/agentes/editar/${$store.getters.getSession.id}`">
          Mi perfil
        </router-link>
      </b-dropdown-item>
      <b-dropdown-item custom aria-role="menuitem">
        <a href="#" @click.prevent="openDashboardNotices">Notificaciones</a>
      </b-dropdown-item>
      <hr class="dropdown-divider" />
      <b-dropdown-item custom aria-role="menuitem">
        <a href="#" @click.prevent="logout">Cerrar sesión</a>
      </b-dropdown-item>
    </b-dropdown>
  </header>

  <nav
    class="mobile-bottom-navigation"
    aria-label="Navegación mobile"
    :style="{ gridTemplateColumns: `repeat(${mobilePrimaryItems.length + 1}, minmax(0, 1fr))` }"
  >
    <component
      v-for="item in mobilePrimaryItems"
      :key="item.key"
      :is="item.children ? 'button' : 'router-link'"
      v-bind="item.children ? { type: 'button' } : { to: item.to }"
      class="mobile-nav-item"
      :class="{ 'is-active': isSectionActive(item) }"
      @click="item.children && openMobileSection(item)"
    >
      <span class="mobile-nav-icon" :class="{ 'is-ventas': item.key === 'ventas' }">
        <template v-if="item.key === 'ventas'">
          <img class="ventas-user" :src="item.icon[0]" alt="" />
          <img class="ventas-plus" :src="item.icon[1]" alt="" />
          <img class="ventas-head" :src="item.icon[2]" alt="" />
        </template>
        <img v-else :src="item.icon" alt="" />
      </span>
      <span>{{ item.label }}</span>
    </component>

    <button
      type="button"
      class="mobile-nav-item"
      :class="{ 'is-active': mobileMoreOpen || mobileOverflowIsActive }"
      aria-label="Ver más opciones"
      :aria-expanded="mobileMoreOpen ? 'true' : 'false'"
      @click="openMobileMore"
    >
      <span class="mobile-nav-icon mobile-more-icon" aria-hidden="true">
        <i class="fas fa-ellipsis-h"></i>
      </span>
      <span>Más</span>
    </button>
  </nav>

  <transition name="mobile-sheet-fade">
    <button
      v-if="mobileMoreOpen"
      type="button"
      class="mobile-sheet-backdrop"
      aria-label="Cerrar menú"
      @click="closeMobileMenu"
    ></button>
  </transition>

  <transition name="mobile-sheet">
    <section v-if="mobileMoreOpen" class="mobile-menu-sheet" aria-label="Más opciones">
      <div class="mobile-sheet-handle"></div>
      <div class="mobile-sheet-header">
        <h2>{{ mobileSheetItem ? mobileSheetItem.label : "Más" }}</h2>
        <button type="button" class="mobile-sheet-close" aria-label="Cerrar" @click="closeMobileMenu">
          <i class="fas fa-times" aria-hidden="true"></i>
        </button>
      </div>

      <div class="mobile-sheet-content">
        <template v-if="mobileSheetItem">
          <template v-for="child in visibleChildren(mobileSheetItem.children || [])">
            <a
              v-if="child.external"
              :key="child.label"
              :href="child.to"
              target="_blank"
              rel="noopener noreferrer"
              class="mobile-sheet-link"
              @click="closeMobileMenu"
            >{{ child.label }}</a>
            <router-link
              v-else
              :key="child.label"
              :to="child.to"
              class="mobile-sheet-link"
              @click.native="closeMobileMenu"
            >{{ child.label }}</router-link>
          </template>
        </template>

        <template v-else>
          <div v-for="item in mobileOverflowItems" :key="item.key" class="mobile-accordion-item">
            <button
              v-if="item.children"
              type="button"
              class="mobile-accordion-trigger"
              :class="{ 'is-open': mobileExpandedKey === item.key }"
              :aria-expanded="mobileExpandedKey === item.key ? 'true' : 'false'"
              @click="toggleMobileSection(item.key)"
            >
              <span class="mobile-sheet-menu-icon" :class="{ 'is-ventas': item.key === 'ventas' }">
                <template v-if="item.key === 'ventas'">
                  <img class="ventas-user" :src="item.icon[0]" alt="" />
                  <img class="ventas-plus" :src="item.icon[1]" alt="" />
                  <img class="ventas-head" :src="item.icon[2]" alt="" />
                </template>
                <img v-else :src="item.icon" alt="" />
              </span>
              <span class="mobile-accordion-label">{{ item.label }}</span>
              <i class="fas fa-chevron-down mobile-accordion-chevron" aria-hidden="true"></i>
            </button>

            <router-link
              v-else
              :to="item.to"
              class="mobile-accordion-trigger"
              @click.native="closeMobileMenu"
            >
              <span class="mobile-sheet-menu-icon">
                <img :src="item.icon" alt="" />
              </span>
              <span class="mobile-accordion-label">{{ item.label }}</span>
            </router-link>

            <transition name="mobile-accordion">
              <div
                v-if="item.children && mobileExpandedKey === item.key"
                class="mobile-accordion-content"
              >
                <template v-for="child in visibleChildren(item.children)">
                  <a
                    v-if="child.external"
                    :key="child.label"
                    :href="child.to"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="mobile-sheet-link"
                    @click="closeMobileMenu"
                  >{{ child.label }}</a>
                  <router-link
                    v-else
                    :key="child.label"
                    :to="child.to"
                    class="mobile-sheet-link"
                    @click.native="closeMobileMenu"
                  >{{ child.label }}</router-link>
                </template>
              </div>
            </transition>
          </div>
        </template>
      </div>
    </section>
  </transition>
  </div>
</template>

<script>
export default {
  name: "Sidebar",

  data() {
    return {
      openMenu: null,
      mobileMoreOpen: false,
      mobileSheetItem: null,
      mobileExpandedKey: null,
      menuItems: [
        { key: "home", label: "Inicio", icon: "/img/sidebar/home.svg", to: "/" },
        {
          key: "consultas",
          label: "Consultas",
          icon: "/img/sidebar/consultas.svg",
          to: "/consultas",
          permission: "consultas.ver",
        },
        {
          key: "ventas",
          label: "Ventas",
          icon: [
            "/img/sidebar/ventas-user.svg",
            "/img/sidebar/ventas-plus.svg",
            "/img/sidebar/ventas-head.svg",
          ],
          to: "/ventas",
          permission: "ventas.ver",
        },
        {
          key: "prestaciones",
          label: "Prestaciones",
          icon: "/img/sidebar/prestaciones.svg",
          permission: "prestadores.ver",
          activePaths: ["/prestadores", "/prestaciones/catalogos"],
          children: [
            {
              label: "Facturación",
              to: { path: "/prestadores", query: { tab: "facturacion" } },
            },
            {
              label: "Listado de prestadores",
              to: { path: "/prestadores", query: { tab: "listado" } },
            },
            { label: "Odontólogos, farmacias y vademécum", to: "/prestaciones/catalogos" },
          ],
        },
        {
          key: "contactos",
          label: "Contactos",
          icon: "/img/sidebar/contactos.svg",
          permission: "contactos.ver",
          activePaths: ["/contactos", "/contacts"],
          children: [
            { label: "Listado completo", to: "/contactos" },
            { label: "Importar", to: "/contactos/importar" },
          ],
        },
        {
          key: "planes",
          label: "Planes",
          icon: "/img/sidebar/planes.svg",
          permission: "cuotasyminimos.ver",
          activePaths: [
            "/landing-planes",
            "/planes",
            "/capita",
            "/monotributo",
            "/vigencia",
            "/adherentes",
          ],
          children: [
            { label: "Listado de planes", to: "/landing-planes" },
            { label: "Cuotas y mínimos", to: "/capita" },
          ],
        },
        {
          key: "organizacion",
          label: "Organización",
          icon: "/img/sidebar/organizacion.svg",
          activePaths: ["/agentes", "/delegaciones", "/departamentos"],
          children: [
            { label: "Agentes", to: "/agentes", permission: "agentes.ver" },
            { label: "Delegaciones", to: "/delegaciones", permission: "delegaciones.ver" },
            {
              label: "Departamentos y permisos",
              to: "/departamentos",
              permission: "departamentos.ver",
            },
          ],
        },
        {
          key: "contenidos",
          label: "Contenidos",
          icon: "/img/sidebar/contenidos.svg",
          permission: "contenidos.ver",
          activePaths: ["/contenidos"],
          children: [
            { label: "Formularios", to: "/contenidos/formularios" },
            { label: "ACP", to: "/contenidos/acp" },
            { label: "Anexos SSSalud", to: "/contenidos/anexos-sssalud" },
          ],
        },
      ],
    };
  },

  computed: {
    avatar() {
      return this.$store.getters.getSession.avatar || this.$avatarDefault;
    },

    visibleMenuItems() {
      return this.menuItems.filter((item) => {
        if (item.permission && !this.hasPermission(item.permission)) return false;
        if (!item.children) return true;
        return this.visibleChildren(item.children).length > 0;
      });
    },

    mobilePrimaryItems() {
      return this.visibleMenuItems.slice(0, 4);
    },

    mobileOverflowItems() {
      return this.visibleMenuItems.slice(4);
    },

    mobileOverflowIsActive() {
      return this.mobileOverflowItems.some((item) => this.isSectionActive(item));
    },
  },

  watch: {
    "$route.fullPath"() {
      this.closeMenu();
      this.closeMobileMenu();
    },
  },

  mounted() {
    document.addEventListener("click", this.closeOnOutsideClick);
  },

  beforeDestroy() {
    document.removeEventListener("click", this.closeOnOutsideClick);
  },

  methods: {
    hasPermission(permission) {
      const permissions = this.$store.getters.getSession.permissions;
      return permissions && permissions.includes(permission);
    },

    visibleChildren(children) {
      return children.filter((child) => !child.permission || this.hasPermission(child.permission));
    },

    buttonBindings(item) {
      return {
        type: "button",
        "aria-expanded": this.openMenu === item.key ? "true" : "false",
        "aria-controls": `${item.key}-submenu`,
      };
    },

    toggleMenu(key) {
      this.openMenu = this.openMenu === key ? null : key;
    },

    closeMenu() {
      this.openMenu = null;
    },

    openDashboardNotices() {
      this.closeMenu();
      this.$store.commit("setDashboardNotices", true);
    },

    openMobileSection(item) {
      this.mobileSheetItem = item;
      this.mobileExpandedKey = null;
      this.mobileMoreOpen = true;
    },

    openMobileMore() {
      if (this.mobileMoreOpen && !this.mobileSheetItem) {
        this.closeMobileMenu();
        return;
      }

      this.mobileSheetItem = null;
      this.mobileExpandedKey = null;
      this.mobileMoreOpen = true;
    },

    toggleMobileSection(key) {
      this.mobileExpandedKey = this.mobileExpandedKey === key ? null : key;
    },

    closeMobileMenu() {
      this.mobileMoreOpen = false;
      this.mobileSheetItem = null;
      this.mobileExpandedKey = null;
    },

    closeOnOutsideClick(event) {
      if (this.openMenu && !this.$el.contains(event.target)) this.closeMenu();
    },

    isSectionActive(item) {
      const paths = item.activePaths || [item.to];
      return paths.some((path) =>
        path === "/" ? this.$route.path === path : this.$route.path.startsWith(path)
      );
    },

    logout() {
      localStorage.clear();
      sessionStorage.clear();
      this.$store.commit("setAuth", false);
      this.$store.commit("setSession", {});
      this.$router.push("/login");
    },
  },
};
</script>

<style lang="scss" scoped>
#sidebar {
  align-items: center;
  background: $white;
  border-right: 1px solid #f2f4f5;
  display: flex;
  flex-direction: column;
  height: 100vh;
  left: 0;
  position: fixed;
  top: 0;
  width: 75px;
  z-index: 20;
}

.sidebar-brand {
  align-items: center;
  border-bottom: 1px solid #f2f4f5;
  display: flex;
  flex: 0 0 70px;
  justify-content: center;
  width: 100%;

  img {
    height: auto;
    max-height: 47px;
    width: 60px;
  }
}

.sidebar-navigation {
  flex: 1;
  min-height: 0;
  padding: 6px 0;
  width: 100%;

  > ul {
    margin: 0;
  }
}

.sidebar-entry {
  margin: 0;
  position: relative;
}

.sidebar-item {
  align-items: center;
  background: $white;
  border: 0;
  border-radius: 0;
  color: #424242;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  font-family: inherit;
  gap: 3px;
  justify-content: center;
  min-height: 60px;
  padding: 6px 3px;
  width: 100%;

  > span:last-child {
    font-size: 7px;
    font-weight: 600;
    line-height: 1.15;
    text-align: center;
    text-transform: uppercase;
  }

  &:hover,
  &:focus,
  &.is-active {
    background: transparent;
    color: $primary;
    outline: none;
  }
}

.sidebar-icon {
  align-items: center;
  display: flex;
  flex: 0 0 34px;
  height: 34px;
  justify-content: center;
  position: relative;
  width: 34px;
  background: transparent;
  border-radius: 50%;
  transition: background-color 0.2s ease;

  img {
    display: block;
    height: 20px;
    max-width: 20px;
    width: auto;
    filter: grayscale(1);
    opacity: 0.78;
    transition: filter 0.2s ease, opacity 0.2s ease;
  }

  &.is-ventas {
    overflow: hidden;

    img {
      position: absolute;
      filter: grayscale(1);
    }

    .ventas-user {
      bottom: 6px;
      height: 9px;
      left: 6px;
      width: 17px;
    }

    .ventas-plus {
      height: 8px;
      right: 6px;
      top: 13px;
      width: 8px;
    }

    .ventas-head {
      height: 11px;
      left: 9px;
      top: 6px;
      width: 11px;
    }
  }
}

.sidebar-item:hover .sidebar-icon,
.sidebar-item:focus .sidebar-icon,
.sidebar-item.is-active .sidebar-icon {
  background: #e7f4f7;

  img {
    filter: none;
    opacity: 1;
  }
}

.sidebar-submenu {
  background: $white;
  border: 1px solid #edf1f3;
  border-radius: 8px;
  box-shadow: 0 5px 14px rgba(66, 66, 66, 0.12);
  left: calc(100% + 7px);
  margin: 0;
  min-width: 190px;
  overflow: hidden;
  padding: 5px;
  position: absolute;
  top: 0;
  z-index: 30;

  li {
    margin: 0;
  }

  a {
    color: #424242;
    display: block;
    font-size: 11px;
    font-weight: 600;
    line-height: 1.25;
    padding: 11px 14px;
    text-transform: uppercase;
    white-space: normal;

    &:hover,
    &:focus,
    &.router-link-exact-active {
      background: #e7f4f7;
      color: $primary;
      border-radius: 5px;
      outline: none;
    }
  }
}

.sidebar-popover-enter-active,
.sidebar-popover-leave-active {
  transition: opacity 0.16s ease, transform 0.16s ease;
  transform-origin: left center;
}

.sidebar-popover-enter,
.sidebar-popover-leave-to {
  opacity: 0;
  transform: translateX(-5px) scale(0.98);
}

.sidebar-footer {
  align-items: center;
  display: flex;
  flex-direction: column;
  padding-bottom: 6px;
  width: 100%;

  .profile-dropdown {
    position: relative;

    ::v-deep .dropdown-menu {
      top: auto;
      bottom: 0;
      left: calc(100% + 8px);
      min-width: 180px;
      padding: 0;
    }

    ::v-deep .dropdown-content {
      overflow: hidden;
      padding: 5px;
      border: 1px solid #edf1f3;
      border-radius: 8px;
      box-shadow: 0 5px 14px rgba(66, 66, 66, 0.12);
    }

    ::v-deep .dropdown-item {
      padding: 10px 12px;
      color: #424242;
      font-family: inherit;
      font-size: 11px;
      font-weight: 600;
      line-height: 1.25;
      text-transform: uppercase;
      border-radius: 5px;

      a {
        color: inherit;
        font: inherit;
        text-transform: inherit;
      }

      &:hover,
      &:focus {
        color: $primary;
        background: #e7f4f7;
      }
    }

    ::v-deep .dropdown-divider {
      margin: 4px 0;
      background: #edf1f3;
    }
  }
}

.profile-trigger {
  background: transparent;
  border: 0;
  cursor: pointer;
  padding: 5px;

  figure {
    height: 40px;
    width: 40px;
  }

  img {
    height: 100%;
    object-fit: cover;
    width: 100%;
  }
}

.zaro-brand {
  display: block;
  padding: 5px 10px;

  img {
    height: auto;
    width: 52px;
  }
}

.mobile-topbar,
.mobile-bottom-navigation,
.mobile-sheet-backdrop,
.mobile-menu-sheet {
  display: none;
}

.sr-only {
  clip: rect(0, 0, 0, 0);
  clip-path: inset(50%);
  height: 1px;
  overflow: hidden;
  position: absolute;
  white-space: nowrap;
  width: 1px;
}

@media (max-height: 760px) {
  .sidebar-item {
    min-height: 54px;
  }

  .sidebar-brand {
    flex-basis: 62px;
  }
}

@media (max-width: 768px) {
  #sidebar {
    display: none;
  }

  .mobile-topbar {
    position: fixed;
    top: 0;
    right: 0;
    left: 0;
    z-index: 1000;
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 54px;
    padding: 6px 14px;
    background: rgba(255, 255, 255, 0.97);
    border-bottom: 1px solid #edf1f3;
    box-shadow: 0 2px 10px rgba(66, 66, 66, 0.05);
  }

  .mobile-brand {
    display: inline-flex;
    align-items: center;

    img {
      width: 63px;
      max-height: 40px;
    }
  }

  .mobile-profile-trigger {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 38px;
    height: 38px;
    padding: 0;
    overflow: hidden;
    background: #edf1f3;
    border: 2px solid #e7f4f7;
    border-radius: 50%;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  .mobile-profile-dropdown {
    ::v-deep .dropdown-menu {
      top: calc(100% + 7px);
      right: 0;
      left: auto;
      min-width: 180px;
      padding: 0;
    }

    ::v-deep .dropdown-content {
      overflow: hidden;
      padding: 5px;
      border: 1px solid #edf1f3;
      border-radius: 8px;
      box-shadow: 0 5px 14px rgba(66, 66, 66, 0.12);
    }

    ::v-deep .dropdown-item {
      padding: 10px 12px;
      color: #424242;
      font-family: inherit;
      font-size: 11px;
      font-weight: 600;
      line-height: 1.25;
      text-transform: uppercase;
      border-radius: 5px;

      a {
        color: inherit;
        font: inherit;
        text-transform: inherit;
      }

      &:hover,
      &:focus {
        color: $primary;
        background: #e7f4f7;
      }
    }
  }

  .mobile-bottom-navigation {
    position: fixed;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 1000;
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    min-height: 64px;
    padding: 5px 4px calc(5px + env(safe-area-inset-bottom));
    background: rgba(255, 255, 255, 0.98);
    border-top: 1px solid #e6ebed;
    box-shadow: 0 -4px 16px rgba(66, 66, 66, 0.08);
  }

  .mobile-nav-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 1px;
    min-width: 0;
    padding: 2px;
    color: #606a6f;
    font-family: inherit;
    background: transparent;
    border: 0;

    > span:last-child {
      max-width: 100%;
      overflow: hidden;
      font-size: 7px;
      font-weight: 600;
      line-height: 1.15;
      text-overflow: ellipsis;
      text-transform: uppercase;
      white-space: nowrap;
    }

    &.is-active {
      color: $primary;

      .mobile-nav-icon {
        background: #e7f4f7;

        img {
          filter: none;
          opacity: 1;
        }
      }
    }
  }

  .mobile-nav-icon {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    color: $primary;
    background: transparent;
    border-radius: 50%;

    > img {
      width: auto;
      max-width: 19px;
      height: 19px;
      filter: grayscale(1);
      opacity: 0.78;
    }

    &.is-ventas {
      overflow: hidden;

      img {
        position: absolute;
        filter: grayscale(1);
        opacity: 0.78;
      }

      .ventas-user {
        bottom: 6px;
        left: 6px;
        width: 17px;
        height: 9px;
      }

      .ventas-plus {
        top: 13px;
        right: 6px;
        width: 8px;
        height: 8px;
      }

      .ventas-head {
        top: 6px;
        left: 9px;
        width: 11px;
        height: 11px;
      }
    }
  }

  .mobile-more-icon i {
    font-size: 16px;
  }

  .mobile-sheet-backdrop {
    position: fixed;
    inset: 0;
    z-index: 1001;
    display: block;
    padding: 0;
    background: rgba(23, 36, 41, 0.32);
    border: 0;
  }

  .mobile-menu-sheet {
    position: fixed;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 1002;
    display: flex;
    flex-direction: column;
    max-height: 72vh;
    padding: 7px 16px calc(14px + env(safe-area-inset-bottom));
    background: $white;
    border-radius: 16px 16px 0 0;
    box-shadow: 0 -8px 28px rgba(23, 36, 41, 0.18);
  }

  .mobile-sheet-handle {
    width: 38px;
    height: 4px;
    margin: 0 auto 8px;
    background: #d7dee1;
    border-radius: 99px;
  }

  .mobile-sheet-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-bottom: 9px;
    border-bottom: 1px solid #edf1f3;

    h2 {
      margin: 0;
      color: $primary;
      font-size: 16px;
      font-weight: 700;
      text-transform: uppercase;
    }
  }

  .mobile-sheet-close {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    color: #606a6f;
    background: #edf1f3;
    border: 0;
    border-radius: 50%;
  }

  .mobile-sheet-content {
    flex: 1;
    padding: 8px 0;
    overflow-y: auto;
  }

  .mobile-accordion-item {
    border-bottom: 1px solid #f0f3f4;
  }

  .mobile-accordion-trigger {
    display: flex;
    align-items: center;
    gap: 11px;
    width: 100%;
    min-height: 48px;
    padding: 6px 8px;
    color: #424242;
    font-family: inherit;
    font-size: 11px;
    font-weight: 700;
    text-align: left;
    text-transform: uppercase;
    background: transparent;
    border: 0;
    border-radius: 8px;

    &:hover,
    &:focus,
    &.is-open,
    &.router-link-exact-active {
      color: $primary;
      background: #e7f4f7;
      outline: none;
    }
  }

  .mobile-sheet-menu-icon {
    position: relative;
    display: flex;
    flex: 0 0 34px;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    background: #eef4f5;
    border-radius: 50%;

    > img {
      width: auto;
      max-width: 19px;
      height: 19px;
      filter: none;
      opacity: 1;
    }

    &.is-ventas {
      overflow: hidden;

      img {
        position: absolute;
      }

      .ventas-user {
        bottom: 6px;
        left: 6px;
        width: 17px;
        height: 9px;
      }

      .ventas-plus {
        top: 13px;
        right: 6px;
        width: 8px;
        height: 8px;
      }

      .ventas-head {
        top: 6px;
        left: 9px;
        width: 11px;
        height: 11px;
      }
    }
  }

  .mobile-accordion-label {
    flex: 1;
  }

  .mobile-accordion-chevron {
    margin-right: 7px;
    color: #7b858a;
    font-size: 10px;
    transition: transform 0.2s ease;

    .is-open & {
      transform: rotate(180deg);
    }
  }

  .mobile-accordion-content {
    padding: 2px 0 8px 45px;

    .mobile-sheet-link {
      padding-left: 12px;
    }
  }

  .mobile-sheet-link {
    display: block;
    padding: 9px 10px;
    color: #4f5b62;
    font-size: 11px;
    font-weight: 600;
    line-height: 1.25;
    text-transform: uppercase;
    border-radius: 6px;

    &:hover,
    &:focus,
    &.router-link-exact-active {
      color: $primary;
      background: #e7f4f7;
    }

  }

  .mobile-accordion-enter-active,
  .mobile-accordion-leave-active {
    max-height: 260px;
    overflow: hidden;
    transition: max-height 0.2s ease, opacity 0.16s ease;
  }

  .mobile-accordion-enter,
  .mobile-accordion-leave-to {
    max-height: 0;
    opacity: 0;
  }

  .mobile-sheet-enter-active,
  .mobile-sheet-leave-active,
  .mobile-sheet-fade-enter-active,
  .mobile-sheet-fade-leave-active {
    transition: opacity 0.2s ease, transform 0.22s ease;
  }

  .mobile-sheet-enter,
  .mobile-sheet-leave-to {
    transform: translateY(100%);
  }

  .mobile-sheet-fade-enter,
  .mobile-sheet-fade-leave-to {
    opacity: 0;
  }
}
</style>
